import type { ContextUsage } from "@utils/contextUsage";
import Dexie, { type Table } from "dexie";
import type { AssistantMessage } from "catchment-common/interfaces/llmQuery";

/**
 * One rendered turn in the assistant transcript. Defined here rather than on
 * the hook because this is the row that gets persisted: the drawer's hook is
 * in `layout/`, and `utils/` may not import a layer above it.
 */
export type WorkflowTranscriptMessage = {
  createdAt: number;
  id: string;
  role: "assistant" | "user";
  text: string;
};

export interface ChatHistoryRow {
  contextUsage?: ContextUsage;
  conversation: AssistantMessage[];
  // Stable id for the conversation, reused across turns and reloads so Laminar
  // groups the whole conversation into one session. Optional: rows written
  // before this field existed simply lack it and get a fresh id on hydrate.
  // Not indexed, so no Dexie schema version bump is required.
  conversationId?: string;
  key: string;
  transcript: WorkflowTranscriptMessage[];
  updatedAt: number;
}

// Anything older than this is treated as stale and not replayed into the
// agent. Stale chats are the main vector for the agent inheriting earlier
// hallucinated facts and reinforcing them — a short freshness window keeps
// each session cheap to drop without losing anything the user cares about.
export const CHAT_HISTORY_FRESHNESS_MS = 2 * 60 * 60 * 1000; // 2 hours

export class ChatHistoryDatabase extends Dexie {
  chatHistory!: Table<ChatHistoryRow, string>;

  constructor() {
    super("SocialProChatHistory");
    this.version(1).stores({
      chatHistory: "key"
    });
    // v2: one-time prune of any rows older than the freshness window. The
    // chat history schema itself doesn't change — this migration exists to
    // make sure no pre-existing poisoned conversation from before the
    // freshness check was added survives the upgrade.
    this.version(2)
      .stores({ chatHistory: "key" })
      .upgrade(async (tx) => {
        const cutoff = Date.now() - CHAT_HISTORY_FRESHNESS_MS;
        await tx
          .table<ChatHistoryRow, string>("chatHistory")
          .filter((row) => (row.updatedAt ?? 0) < cutoff)
          .delete();
      });
  }
}

export const chatHistoryDb = new ChatHistoryDatabase();

export const chatHistoryKey = (orgId: string, userId: string): string =>
  `${orgId}:${userId}`;
